import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { fetchClients } from '@/features/clients/api'
import { fetchPeople, type Person } from '@/features/people/api'
import {
  archiveProject,
  setProjectStage,
  updateProject,
  type ProjectChanges,
} from '@/features/portfolio/api'
import { fetchWorkflows } from '@/features/workflow/api'
import type { ApiError } from '@/lib/api/client'
import type { Project } from '@/types/domain'
import type { Client } from '@/types/support'
import type { Workflow } from '@/types/workflow'

/**
 * Everything about a project that is not the work in it.
 *
 * A project used to be write-once: named at creation and never corrected. The
 * division is deliberately absent — the human key was drawn from it and every
 * reference to AIS-14 outlives the project's own record, so moving one between
 * divisions is not a field edit.
 *
 * The stage saves on its own because it is a move through the engagement
 * workflow rather than a field, and it is what the portfolio pipeline counts.
 */
export function ProjectSettings({ project, onClose }: { project: Project; onClose: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    name: project.name,
    clientId: project.clientId ?? '',
    leadUserId: project.leadUserId ?? '',
    description: project.description ?? '',
    solution: project.solution ?? '',
    startDate: project.startDate?.slice(0, 10) ?? '',
    targetDate: project.targetDate?.slice(0, 10) ?? '',
    valueOmr: project.valueOmr === null ? '' : String(project.valueOmr),
    budgetOmr: project.budgetOmr === null ? '' : String(project.budgetOmr),
  })

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  const clients = useQuery<Client[], ApiError>({ queryKey: ['clients'], queryFn: fetchClients, retry: false })
  const people = useQuery<Person[], ApiError>({ queryKey: ['people'], queryFn: fetchPeople, retry: false })
  const workflows = useQuery<Workflow[], ApiError>({
    queryKey: ['workflows'],
    queryFn: fetchWorkflows,
    retry: false,
  })

  const stages =
    workflows.data?.find((workflow) => workflow.id === project.engagementWorkflowId)?.states ?? []

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['project', project.key] })
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
    void queryClient.invalidateQueries({ queryKey: ['portfolio'] })
    void queryClient.invalidateQueries({ queryKey: ['activities'] })
  }

  const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim())

  const save = useMutation<Project, ApiError, void>({
    mutationFn: () => {
      const changes: ProjectChanges = {
        name: form.name.trim(),
        clientId: blankToNull(form.clientId),
        leadUserId: blankToNull(form.leadUserId),
        description: blankToNull(form.description),
        solution: blankToNull(form.solution),
        startDate: blankToNull(form.startDate),
        targetDate: blankToNull(form.targetDate),
        valueOmr: blankToNull(form.valueOmr),
        budgetOmr: blankToNull(form.budgetOmr),
      }

      return updateProject(project.key, changes)
    },
    onSuccess: () => {
      refresh()
      onClose()
    },
  })

  const stage = useMutation<Project, ApiError, string>({
    mutationFn: (stageId) => setProjectStage(project.key, stageId),
    onSuccess: refresh,
  })

  const archive = useMutation<Project, ApiError, boolean>({
    mutationFn: (next) => archiveProject(project.key, next),
    onSuccess: () => {
      refresh()
      onClose()
    },
  })

  const error = save.error ?? stage.error ?? archive.error

  return (
    <Dialog title={t('project.settings')} onClose={onClose}>
      <form
        className="flex max-h-[70vh] flex-col gap-3.5 overflow-y-auto px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (form.name.trim() !== '') save.mutate()
        }}
      >
        <Field label={t('portfolio.projectName')}>
          <input
            value={form.name}
            autoFocus
            onChange={(event) => set('name')(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        {stages.length === 0 ? null : (
          <Field label={t('project.stage')}>
            <select
              value={project.stageId ?? ''}
              disabled={stage.isPending}
              onChange={(event) => stage.mutate(event.target.value)}
              className={FIELD_CLASS}
            >
              <option value="">—</option>
              {stages.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3.5">
          <Field label={t('portfolio.client')}>
            <select
              value={form.clientId}
              onChange={(event) => set('clientId')(event.target.value)}
              className={FIELD_CLASS}
            >
              <option value="">{t('portfolio.noClient')}</option>
              {(clients.data ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('project.lead')}>
            <select
              value={form.leadUserId}
              onChange={(event) => set('leadUserId')(event.target.value)}
              className={FIELD_CLASS}
            >
              <option value="">{t('project.noLead')}</option>
              {(people.data ?? []).map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('project.startDate')}>
            <input
              type="date"
              value={form.startDate}
              onChange={(event) => set('startDate')(event.target.value)}
              className={FIELD_CLASS}
            />
          </Field>

          <Field label={t('project.targetDate')}>
            <input
              type="date"
              value={form.targetDate}
              onChange={(event) => set('targetDate')(event.target.value)}
              className={FIELD_CLASS}
            />
          </Field>

          <Field label={t('project.value')}>
            <input
              inputMode="decimal"
              value={form.valueOmr}
              onChange={(event) => set('valueOmr')(event.target.value)}
              className={`${FIELD_CLASS} font-mono tabular-nums`}
            />
          </Field>

          <Field label={t('project.budget')}>
            <input
              inputMode="decimal"
              value={form.budgetOmr}
              onChange={(event) => set('budgetOmr')(event.target.value)}
              className={`${FIELD_CLASS} font-mono tabular-nums`}
            />
          </Field>
        </div>

        <Field label={t('project.description')}>
          <textarea
            value={form.description}
            rows={2}
            onChange={(event) => set('description')(event.target.value)}
            className={`${FIELD_CLASS} resize-none`}
          />
        </Field>

        <Field label={t('project.solution')}>
          <textarea
            value={form.solution}
            rows={2}
            onChange={(event) => set('solution')(event.target.value)}
            className={`${FIELD_CLASS} resize-none`}
          />
        </Field>

        {error === null || error === undefined ? null : (
          <p className="text-[12px] text-[color:var(--color-state-blocked)]">{error.message}</p>
        )}

        <div className="border-line flex items-center gap-3 border-t pt-3.5">
          <button
            type="button"
            onClick={() => archive.mutate(!project.archived)}
            disabled={archive.isPending}
            title={t('project.archiveNote')}
            className="text-ink-500 hover:text-[color:var(--color-state-blocked)] text-[12px] underline disabled:opacity-40"
          >
            {project.archived ? t('project.unarchive') : t('project.archive')}
          </button>

          <button
            type="submit"
            disabled={save.isPending || form.name.trim() === ''}
            className="bg-brand-600 ms-auto rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
          >
            {save.isPending ? t('project.saving') : t('project.save')}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
